<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Modal, Text, IconButton, Popover } from '@Shelly/shelly-design-system';
	import type { EnergyTariff } from 'src/types/EnergyTariff';

	export let showModal: boolean = false;
	export let benchmarkTariff: EnergyTariff | null = null;

	const handleClose = () => {
		showModal = false;
	};

	// Helper function to format numbers with comma as decimal separator
	const formatNumber = (value: number, decimals: number = 2): string => {
		return value.toFixed(decimals).replace('.', ',');
	};

	// Calculate monthly average
	$: monthlyAverage = benchmarkTariff?.totalCosts
		? formatNumber(benchmarkTariff.totalCosts / 12)
		: '0,00';

	// Format price per kWh as ct/kWh
	$: pricePerKwhCents = benchmarkTariff?.pricePerKwh
		? formatNumber(benchmarkTariff.pricePerKwh * 100)
		: '0,00';

	// Format base price as €/Monat (monthly)
	$: basePriceMonthly = benchmarkTariff?.basePrice
		? formatNumber(benchmarkTariff.basePrice / 12)
		: '0,00';

	let showMonthlyAveragePopover = false;
</script>

<Modal {showModal} on:close={handleClose} hideTitle backdropClose={true}>
	<svelte:fragment slot="content">
		<Text styleType="h2" class="modal-title mb-m">
			{$format('energy_price_benchmark_tariff_title', {
				default: 'Ihr Vergleichstarif zur Berechnung der Einsparungen',
				locale: 'de',
			})}
		</Text>
		{#if benchmarkTariff}
			<div class="tariff-card">
				<div class="tariff-content">
					<div class="tariff-left-column">
						{#if benchmarkTariff.provider}
							<div class="info-row">
								<Text styleType="body-small" class="info-label">
									{$format('label_provider', { default: 'Anbieter', locale: 'de' })}:
								</Text>
								<Text styleType="body" class="info-value">
									{benchmarkTariff.provider}
								</Text>
							</div>
						{/if}

						{#if benchmarkTariff.name && benchmarkTariff.name !== benchmarkTariff.provider}
							<div class="info-row">
								<Text styleType="body-small" class="info-label">
									{$format('label_rate', { default: 'Tarif', locale: 'de' })}:
								</Text>
								<Text styleType="body" class="info-value">
									{benchmarkTariff.name}
								</Text>
							</div>
						{/if}
					</div>

					<div class="tariff-right-column">
						{#if benchmarkTariff.pricePerKwh !== undefined && benchmarkTariff.pricePerKwh > 0}
							<div class="info-row">
								<Text styleType="body-small" class="info-label">
									{$format('label_labor_price', {
										default: 'Arbeitspreis',
										locale: 'de',
									})}:
								</Text>
								<Text styleType="body" class="info-value price-bold">
									{pricePerKwhCents} ct/kWh
								</Text>
							</div>
						{/if}

						{#if benchmarkTariff.basePrice !== undefined && benchmarkTariff.basePrice > 0}
							<div class="info-row">
								<Text styleType="body-small" class="info-label">
									{$format('label_base_price', {
										default: 'Grundpreis',
										locale: 'de',
									})}:
								</Text>
								<Text styleType="body" class="info-value price-bold">
									{basePriceMonthly} €/Monat
								</Text>
							</div>
						{/if}
					</div>

					<div class="tariff-monthly-average">
						<Text styleType="h1" class="monthly-amount">
							{monthlyAverage} €
						</Text>
						<div class="monthly-average-info">
							<Text styleType="body-small" class="monthly-label">
								{$format('label_average_per_month', {
									default: 'Durchschnitt pro Monat',
									locale: 'de',
								})}
							</Text>
							<Popover
								hideCloseBtn={true}
								show={showMonthlyAveragePopover}
								placement="top"
								type="hover"
								on:close={() => (showMonthlyAveragePopover = false)}
							>
								<IconButton
									icon="fa-solid fa-circle-info"
									variant="help"
									size="s"
									class="info-icon"
									on:click={() => {
										showMonthlyAveragePopover = !showMonthlyAveragePopover;
									}}
									aria-label={$format('energy_price_monthly_average_info', {
										default: 'Informationen zum monatlichen Durchschnitt',
										locale: 'de',
									})}
								/>
								<svelte:fragment slot="customContent">
									<div class="popover-content">
										<Text styleType="body" class="popover-title">
											{$format('energy_price_monthly_average_info_title', {
												default: 'Kosten des Vergleichstarifs',
												locale: 'de',
											})}
										</Text>
										<Text styleType="body-small" class="popover-description">
											{$format('energy_price_monthly_average_info_description', {
												default:
													'Die monatlichen Kosten des Vergleichstarifs werden auf Basis der aktuellen Preise Ihres ausgewählten bestehenden Tarifs berechnet. Diese Preise können von den Kosten abweichen, die Sie derzeit zahlen. Handelt es sich um einen Grundversorgungstarif, können während des für die Vergleichsberechnung verwendeten Zeitraums Preisänderungen an den Vergleichstarifen auftreten. In diesem Fall ändern sich die angegebenen monatlichen Kosten des Vergleichstarifs und die angegebenen Preisersparnisse.',
												locale: 'de',
											})}
										</Text>
									</div>
								</svelte:fragment>
							</Popover>
						</div>
					</div>
				</div>

				<div class="tariff-note">
					<Text styleType="body-small">
						{@html $format('energy_price_benchmark_tariff_note', {
							default:
								'<strong>Hinweis:</strong> Grundversorgung (mit gesetzlich garantierten 2 Wochen Kündigungsfrist) als Vergleichstarif voreingestellt und für Sie jederzeit änderbar. Es kann Preisanpassungen während der Vertragslaufzeit bei der Grundversorgung geben. Hierdurch ändert sich die angegebene Preisersparnis.',
							locale: 'de',
						})}
					</Text>
				</div>
			</div>
		{/if}
	</svelte:fragment>
</Modal>

<style>
	.tariff-card {
		display: flex;
		flex-direction: column;
	}

	.tariff-content {
		display: grid;
		grid-template-columns: 1fr 1fr auto;
		gap: var(--spacing-l);
		align-items: start;
	}

	.tariff-left-column,
	.tariff-right-column {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
	}

	.info-row {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.info-label {
		color: var(--color-text-secondary, #6b7280);
		font-weight: 400;
	}

	.info-value {
		color: var(--color-text-primary, #000000);
		font-weight: 400;
	}

	.info-value.price-bold {
		font-weight: 700;
	}

	.tariff-monthly-average {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: flex-start;
		gap: var(--spacing-xs);
		min-width: 120px;
	}

	.monthly-amount {
		font-size: 32px;
		font-weight: 700;
		line-height: 1;
		color: var(--color-text-primary, #000000);
		margin: 0;
	}

	.monthly-average-info {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
	}

	.monthly-label {
		color: var(--color-text-secondary, #6b7280);
		font-size: 12px;
	}

	.info-icon {
		transform: scale(0.85);
	}

	.popover-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.popover-title {
		font-weight: 600;
		margin: 0;
	}

	.popover-description {
		margin: 0;
		line-height: 1.5;
	}

	.tariff-note {
		background: var(--color-background-info, #e0f2fe);
		padding: var(--spacing-m);
		border-radius: 8px;
		margin-top: var(--spacing-m);
	}

	.tariff-note :global(p) {
		margin: 0;
		color: var(--color-text-secondary, #6b7280);
		line-height: 1.5;
	}

	@media (max-width: 768px) {
		.tariff-content {
			grid-template-columns: 1fr;
			gap: var(--spacing-m);
		}

		.tariff-monthly-average {
			align-items: flex-start;
			width: 100%;
		}

		.monthly-amount {
			font-size: 28px;
		}
	}
</style>
